<template>
    <div
        :class="{
            'tw-flex tw-flex-wrap tw-flex-row tw-items-center lg:tw-w-full lg:tw-h-full': true,
            relative: !isSearchBarOpen,
        }"
        role="search"
        @keyup.enter="onKeyUpEnter"
        @keyup.up="onKeyUpPage(-1)"
        @keyup.down="onKeyUpPage(1)"
    >
        <div
            class="tw-absolute lg:tw-static tw-top-0 tw-left-4 tw-right-14 tw-h-full lg:tw-flex-auto"
            :class="{ 'tw-hidden': !isSearchBarOpen }"
        >
            <input
                ref="readOnlyInput"
                :value="readonlyQuery"
                type="search"
                class="tw-absolute tw-top-0 tw-left-0 -tw-z-[1] tw-text-transparent tw-h-full tw-w-full tw-text-sm tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-appearance-none tw-bg-transparent tw-border-none tw-outline-0"
                :class="{
                    'ts--normal-case': !readonlyQuery,
                    'ts--lowercase': readonlyQuery,
                }"
                readonly="readonly"
            >

            <input
                ref="realInput"
                v-model="query"
                :placeholder="$t('search-bar.search')"
                type="search"
                class="tw-h-full tw-w-full tw-text-sm tw-text-black tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-appearance-none tw-bg-transparent tw-border-none tw-outline-0"
                :class="{
                    'ts--normal-case': !query,
                    'ts--lowercase': query,
                }"
                @blur="trackSearchKeyWords"
                @keydown.up.prevent="$event.preventDefault()"
            >
        </div>

        <dk-button
            class="masthead__menu-item masthead__search-icon !tw-w-fit !tw-h-fit lg:tw-mr-2 lg:tw-opacity-[.72] lg:hover:tw-opacity-100"
            :class="{ 'tw-hidden': isSearchBarOpen }"
            :is-icon-only="true"
            :is-inverse="true"
            variant="ghost"
            @click="toggleSearchBar"
        >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="tw-h-6 tw-w-6">
                <g clip-path="url(#clip0_2091_1374)">
                    <path
                        d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_2091_1374">
                        <rect width="24" height="24" />
                    </clipPath>
                </defs>
            </svg>
        </dk-button>

        <dk-button
            class="masthead__menu-item masthead__search-icon -tw-mr-3 lg:tw-mr-0 tw-ml-auto !tw-text-black lg:tw-opacity-[.72] lg:hover:tw-opacity-100"
            :class="{ 'tw-hidden': !isSearchBarOpen }"
            :is-icon-only="true"
            :is-inverse="true"
            variant="ghost"
            @click="toggleSearchBar"
        >
            <svg xmlns="http://www.w3.org/2000/svg" class="tw-h-6 tw-w-6" viewBox="0 0 20 20" fill="currentColor">
                <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                />
            </svg>
        </dk-button>

        <div
            ref="dropdown"
            class="search-bar__dropdown tw-top-header"
            :class="{ 'tw-block': isDropdownVisible }"
            @mouseleave="onLeave()"
        >
            <ul v-if="isDropdownVisible">
                <li
                    v-if="hasNoResults"
                    class="searchlist-item searchlist-item--empty"
                    v-html="$t('search-bar.empty-results', [query])"
                ></li>
                <template v-for="(suggestion, index) in suggestionsList">
                    <li
                        v-if="suggestion.label"
                        :key="index"
                        :class="{
                            'searchlist-item': true,
                            'searchlist-item--result': true,
                            'has-focus': index === focusedItemIndex,
                        }"
                        @click="navigateTo(suggestion)"
                        @mouseover="onHover(suggestion, index)"
                    >
                        <span v-html="printSuggestion(suggestion, index)"></span>
                        <span v-if="suggestion.isElectric" class="searchlist-item__icon">
                            <icon name="thunder" size="xs" color="white" />
                        </span>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script>
import Icon from '@v4/common/components/atoms/Icon';
import DkButton from '@v4/common/components/atoms/DkButton';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import 'element-closest';
import { ajax } from 'rxjs/ajax';
import { fromEvent, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    filter,
    switchMap,
    catchError,
    finalize,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { config } from '@v4/config';
import { findAll } from 'highlight-words-core';

export default {
    name: 'SearchBar',
    components: {
        Icon,
        DkButton,
    },
    mixins: [gaEventHandler],
    data() {
        return {
            isSearchBarOpen: false,
            isVisible: false,
            isBusy: false,
            items: [],
            query: '',
            originalQuery: '',
            focusedItemIndex: -1,
            isNavigatingUpOrDown: false,
            inputSubscription: null,
            documentClickSubscription: null,
            suggestionsList: [],
            destroy$: new Subject(),
        };
    },
    computed: {
        isDropdownVisible() {
            return (this.suggestionsList.length || this.hasNoResults) && this.isVisible;
        },
        realInput() {
            return this.$refs.realInput;
        },
        readOnlyInput() {
            return this.$refs.readOnlyInput;
        },
        dropdown() {
            return this.$refs.dropdown;
        },
        readonlyQuery() {
            if (
                this.query &&
                this.suggestionsList.length &&
                !this.isNavigatingUpOrDown &&
                new RegExp(`^${this.query}`, 'i').test(this.suggestionsList[0].label)
            ) {
                return this.suggestionsList[0].label;
            }
            return '';
        },
        hasNoResults() {
            return this.suggestionsList.length === 0 && this.query && !this.isBusy;
        },
    },
    destroyed() {
        this.removeSubscriptions();
    },
    methods: {
        onDocumentClick() {
            this.documentClickSubscription = fromEvent(document, 'click')
                .pipe(
                    map((e) => e.target),
                    filter((target) => target)
                )
                .subscribe((target) => {
                    const closest = target.closest('.search-bar__dropdown');
                    if (!closest && this.isDropdownVisible) {
                        this.isVisible = false;
                    }
                });
        },
        removeSubscriptions() {
            // Remove subscription to input event.
            this.inputSubscription && this.inputSubscription.unsubscribe();
            // Remove document click listener
            this.documentClickSubscription && this.documentClickSubscription.unsubscribe();
            // Destroy
            this.destroy$.next();
            this.destroy$.complete();
        },
        toggleSearchBar() {
            this.isSearchBarOpen = !this.isSearchBarOpen;
            this.query = '';
            this.originalQuery = '';
            this.$emit('toggle', this.isSearchBarOpen);

            if (!this.isSearchBarOpen) {
                this.isVisible = false;
            }

            this.pushGtagEvent('click_start_search', null, null, {
                // eslint-disable-next-line camelcase
                current_page: window.location.href,
            });

            if (this.isSearchBarOpen) {
                this.realInput && this.realInput.focus();
            } else {
                this.realInput && this.realInput.blur();
                this.removeSubscriptions();
                return;
            }

            this.onDocumentClick();

            this.inputSubscription = fromEvent(this.realInput, 'input')
                .pipe(
                    tap(() => {
                        this.isNavigatingUpOrDown = false;
                        this.originalQuery = '';
                    }),
                    debounceTime(150),
                    map((e) => e.target.value),
                    tap((term) => {
                        if (!term) {
                            this.suggestionsList = [];
                        }
                    }),
                    filter((term) => term),
                    distinctUntilChanged(),
                    switchMap((term) => {
                        this.isBusy = true;
                        const url = `${config.API_URL}/dkwp/v1/search-bar`;
                        return ajax
                            .post(url, {
                                term,
                                href: window.location.href,
                            })
                            .pipe(
                                takeUntil(this.destroy$),
                                map((ajaxResponse) => ajaxResponse.response.data),
                                catchError(() => of([])),
                                finalize(() => {
                                    this.focusedItemIndex = -1;
                                    this.isBusy = false;
                                })
                            );
                    }),
                    finalize(() => {
                        this.isNavigatingUpOrDown = false;
                    })
                )
                .subscribe(
                    (data) => {
                        this.isVisible = true;
                        this.suggestionsList = data;
                    },
                    (data) => {
                        this.isVisible = true;
                        this.suggestionsList = data;
                    }
                );
        },
        printSuggestion(suggestion) {
            const { label } = suggestion;
            const chunks = findAll({
                searchWords: this.query.split(' '),
                textToHighlight: label,
            });
            return chunks
                .map((chunk) => {
                    const { end, highlight, start } = chunk;
                    const text = label.substr(start, end - start);
                    if (highlight) {
                        return `<strong>${text}</strong>`;
                    } else {
                        return text;
                    }
                })
                .join('');
        },
        async navigateTo(suggestion) {
            this.trackSearchSuggestionClick(suggestion.label, suggestion.searchKey);
            let url = suggestion.href;

            if (!url) {
                const data = {
                    href: window.location.href,
                    key: suggestion.searchKey,
                    value: suggestion.searchValue,
                };
                url = await new Promise((resolve) => {
                    ajax.post(`${config.API_URL}/dkwp/v1/search-url`, data)
                        .pipe(
                            takeUntil(this.destroy$),
                            map((ajaxResponse) => ajaxResponse.response.data),
                            catchError(() => null)
                        )
                        .subscribe((destinationUrl) => resolve(destinationUrl));
                });
            }

            url && window.location.assign(url);
        },
        onKeyUpEnter() {
            let focused = this.dropdown.querySelector('li.searchlist-item--result.has-focus');
            if (!focused) {
                focused = this.dropdown.querySelector('li.searchlist-item--result');
            }
            focused && focused.click();
        },
        onKeyUpPage(direction) {
            this.isNavigatingUpOrDown = true;
            if (this.focusedItemIndex < this.suggestionsList.length - 1 && this.focusedItemIndex >= 0) {
                if (direction > 0) {
                    this.focusedItemIndex += direction;
                } else {
                    if (this.focusedItemIndex === 0) {
                        this.focusedItemIndex = this.suggestionsList.length - 1;
                    } else {
                        this.focusedItemIndex += direction;
                    }
                }
            } else if (this.focusedItemIndex >= this.suggestionsList.length - 1) {
                if (direction > 0) {
                    this.focusedItemIndex = 0;
                } else {
                    this.focusedItemIndex += direction;
                }
            } else if (this.focusedItemIndex === -1) {
                if (direction > 0) {
                    this.focusedItemIndex += direction;
                } else {
                    this.focusedItemIndex = this.suggestionsList.length - 1;
                }
            }
            if (!this.originalQuery) {
                this.originalQuery = this.query;
            }
            this.query = this.suggestionsList[this.focusedItemIndex].label;
        },
        onHover(suggestion, index) {
            if (!this.originalQuery) {
                this.originalQuery = this.query;
            }
            this.isNavigatingUpOrDown = true;
            this.query = suggestion.label;
            this.focusedItemIndex = index;
        },
        onLeave() {
            this.query = this.originalQuery;
            this.originalQuery = '';
            this.focusedItemIndex = -1;
        },
        trackSearchKeyWords({ target: { value } }) {
            this.pushGaEvent('website search', `current page URL: ${window.location.href}`, `keyword: ${value}`, value);
        },
        trackSearchSuggestionClick(suggestionKeyword, suggestionCategory) {
            this.pushGaEvent(
                'website search - suggestion click',
                `current page URL: ${window.location.href}`,
                `suggestion keyword and category: ${suggestionKeyword}, ${suggestionCategory}`
            );

            this.pushGtagEvent('search', null, null, {
                // eslint-disable-next-line camelcase
                search_term: suggestionKeyword,
                // eslint-disable-next-line camelcase
                current_page: window.location.href,
            });
        },
    },
};
</script>
